/**
 * StimSheetView — stimulation monitoring flowsheet and entry surface.
 *
 * Prototype for issue #2001: reconciles the table, the line-chart trend, and
 * the follicle bar idea into one readable, phone-native stim sheet on real
 * hooks. Measurement cells open a focused editor for that metric and day, so
 * the table and the dedicated entry surface share one write path.
 *
 * Design goals demonstrated here:
 *  - a frozen metric column with only the day columns scrolling horizontally
 *  - a "glance" strip that tells the cycle story before the dense grid
 *  - every value carries a trend vs the previous scan and a normal/abnormal tint
 *  - follicles shown as a per-ovary cohort (lead bold, size ranges colored)
 *  - medications share the same day timeline as the labs (a real stim sheet)
 */

import { useMemo, useState } from "react";
import { Image, View, Text, ScrollView, Pressable } from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import {
  useTestResults,
  type TestResult,
} from "@src/packages/test-results/hooks/useTestResults";
import type { ApiPhase } from "@src/packages/patients/hooks/usePhases";
import type { CycleMonitoringEncounter } from "@src/packages/care/hooks/useCycleMonitoring";
import { usePatientMedications } from "@src/packages/care/hooks/usePatientMedications";
import { useMedicationLogs } from "@src/packages/care/hooks/useMedicationLogs";
import {
  getTodayDoseStatus,
  type DoseStatus,
} from "@src/packages/care/medications/doseStatusStyle";
import { isTriggerMedicationName } from "@src/packages/care/medications/trigger";
import { useTranslations, useLocale } from "@src/packages/i18n";
import { formatDate } from "@src/packages/i18n/formatters";
import {
  ActionRowCard,
  CardSubtitleText,
  CardTitleText,
} from "@louise/moon-ui";
import { Plus } from "lucide-react-native";
import { OrganicMiniIcon } from "@src/packages/homepage/plan/components/OrganicMiniIcon";
import { EMBER } from "../../../../onboarding/theme";
import { GLASS_CARD_STYLE } from "@louise/moon-ui/theme/tokens";
import { toLocalDateString } from "@src/packages/utils/date";
import { calendarDayOf } from "@src/packages/care/prediction/menstrualBleeding";
import {
  monitoringDisplayResultKey,
  monitoringEncounterDisplayResults,
  monitoringResultsForCycle,
  type MonitoringDisplayResult,
} from "./stimMonitoringEntry";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

const COPPER = "#B84A16";
const TEXT_MID = "#5A4D45";
const TEXT_LIGHT = "#8A7D74";
const LARGER = "#B08030"; // >= 17mm
const MID_RANGE = "#8B6F5E"; // 14-16mm
const SMALLER = "rgba(90,77,69,0.28)"; // < 14mm
const ABNORMAL = "#B4552B";
const STATUS_COLOR: Record<DoseStatus, string> = {
  taken: "#82D4A8",
  late: "#F5C882",
  missed: "#E8927C",
  skipped: TEXT_LIGHT,
};

const LABEL_W = 96;
const DAY_W = 78;
const MATURE_MM = 17;
const COHORT_MM = 14;
const MONITORING_ILLUSTRATION =
  require("@src/apps/moon/assets/images/monitoring-dialog-illustration.png") as number;

type LabCell = {
  text: string;
  num: number | null;
  assessment: TestResult["clinicalAssessment"];
};

type MedChip = {
  key: string;
  name: string;
  dose: string | null;
  status?: DoseStatus;
  isTrigger: boolean;
};

/**
 * How many columns a stimulation sheet shows when the clinic has not recorded
 * an end for the protocol phase. An ordinary stimulation runs roughly this
 * long, and the sheet is a surface you fill day by day, so it has to show the
 * days ahead of the one you are on.
 */
const DEFAULT_STIM_DAYS = 14;

/**
 * Day arithmetic on the calendar, not on a duration. Adding milliseconds to a
 * timestamp crosses a DST boundary and lands on the same wall-clock day twice,
 * or skips one; setDate() on a local date moves whole days regardless.
 */
function addDaysISO(dateISO: string, days: number): string | null {
  const d = new Date(dateISO + "T00:00:00");
  // An unparseable date answered "NaN-NaN-NaN", which then compared as the
  // largest string of all — larger than any real date, because "N" sorts after
  // any digit. It sailed through a `horizon >= rangeStart` check and became the
  // range's end. Refusing it here is the only place the question is cheap.
  if (Number.isNaN(d.getTime())) return null;
  d.setDate(d.getDate() + days);
  return toLocalDateString(d);
}

type StimDay = {
  dateISO: string;
  stimDay: number;
  dayLabel: string;
  dateLabel: string;
  e2: LabCell | null;
  lh: LabCell | null;
  p4: LabCell | null;
  lining: LabCell | null;
  follLeft: number[];
  follRight: number[];
  lead: number | null;
  larger: number;
  cohort: number;
  follTotal: number | null;
  meds: MedChip[];
  isTriggerDay: boolean;
};

type SheetRow = {
  key: string;
  label: string;
  height: number;
};

interface StimSheetViewProps {
  cycleId: string | null | undefined;
  cycleStartDate: string | null | undefined;
  phases: ApiPhase[];
  monitoringEncounters?: CycleMonitoringEncounter[];
  includeUnlinkedResults?: boolean;
  onAddDay?: () => void;
  onEditCell?: (dateISO: string, cellKey: string) => void;
  onDataStateChange?: (hasData: boolean) => void;
}

function stimDayNumber(dateISO: string, referenceStart: string): number {
  const day = new Date(dateISO + "T00:00:00").getTime();
  const start = new Date(referenceStart + "T00:00:00").getTime();
  const offset = Math.floor((day - start) / 86400000);
  return offset >= 0 ? offset + 1 : offset;
}

function parseSizes(raw: string | null | undefined): number[] {
  if (!raw) return [];
  return raw
    .split(",")
    .map((s) => parseFloat(s.trim()))
    .filter((n) => !Number.isNaN(n))
    .sort((a, b) => b - a);
}

function resolvedValue(tr: MonitoringDisplayResult): string {
  return tr.doctorValue ?? tr.patientValue ?? tr.value ?? "";
}

const PATTERNS = {
  e2: /estradiol|^e2$/i,
  lh: /^lh$/i,
  p4: /progesterone|^p4$/i,
  lining: /endometrial|lining|thickness/i,
  sizesLeft: /follicle\s*sizes?\s*left/i,
  sizesRight: /follicle\s*sizes?\s*right/i,
  countLeft: /follicle\s*count\s*left/i,
  countRight: /follicle\s*count\s*right/i,
};

export default function StimSheetView({
  cycleId,
  cycleStartDate,
  phases,
  monitoringEncounters,
  includeUnlinkedResults = false,
  onAddDay,
  onEditCell,
  onDataStateChange,
}: StimSheetViewProps) {
  const t = useTranslations();
  const { locale } = useLocale();
  const { data: testResults } = useTestResults();
  const { data: medications } = usePatientMedications();
  const { data: medicationLogs } = useMedicationLogs();
  const [viewportW, setViewportW] = useState(0);
  const [contentW, setContentW] = useState(0);
  const [scrollX, setScrollX] = useState(0);

  const days = useMemo<StimDay[]>(() => {
    // Per-date accumulator seeded from every source.
    type Acc = {
      e2?: LabCell;
      lh?: LabCell;
      p4?: LabCell;
      lining?: LabCell;
      sizesL: number[];
      sizesR: number[];
      sizesLFromResult: boolean;
      sizesRFromResult: boolean;
      countL: number | null;
      countR: number | null;
    };
    const acc = new Map<string, Acc>();
    const encounterResults = monitoringEncounterDisplayResults(
      monitoringEncounters,
      cycleId,
    );
    const encounterResultKeys = new Set(
      encounterResults.map(monitoringDisplayResultKey),
    );
    const ensure = (dateISO: string): Acc => {
      let a = acc.get(dateISO);
      if (!a) {
        a = {
          sizesL: [],
          sizesR: [],
          sizesLFromResult: false,
          sizesRFromResult: false,
          countL: null,
          countR: null,
        };
        acc.set(dateISO, a);
      }
      return a;
    };

    // Ingest one lab/imaging result into the day accumulator. Shared by the
    // patient test-results hook and the clinic-authored phase.testResults.
    const ingestResult = (
      a: Acc,
      name: string,
      raw: string,
      assessment: TestResult["clinicalAssessment"],
    ) => {
      const num = parseFloat(raw);
      const cell: LabCell = {
        text: raw,
        num: Number.isNaN(num) ? null : num,
        assessment,
      };
      if (PATTERNS.e2.test(name)) {
        a.e2 = cell;
      } else if (PATTERNS.lh.test(name)) {
        a.lh = cell;
      } else if (PATTERNS.p4.test(name)) {
        a.p4 = cell;
      } else if (PATTERNS.lining.test(name)) {
        a.lining = cell;
      } else if (PATTERNS.sizesLeft.test(name)) {
        a.sizesL = parseSizes(raw);
        a.sizesLFromResult = true;
      } else if (PATTERNS.sizesRight.test(name)) {
        a.sizesR = parseSizes(raw);
        a.sizesRFromResult = true;
      } else if (PATTERNS.countLeft.test(name) && !Number.isNaN(num)) {
        a.countL = num;
      } else if (PATTERNS.countRight.test(name) && !Number.isNaN(num)) {
        a.countR = num;
      }
    };

    // Source 1: cycle monitoring encounters (the canonical patient/clinic
    // monitoring record).
    for (const tr of encounterResults) {
      const dateISO = tr.date!.split("T")[0]!;
      const a = ensure(dateISO);
      ingestResult(a, tr.name ?? "", resolvedValue(tr), null);
    }

    // Source 2: patient test results kept for older cycles and compatibility.
    const cycleResults = monitoringResultsForCycle(testResults, cycleId, {
      cycleStartDate,
      includeUnlinkedResults,
    }).filter(
      (tr) =>
        tr.date && !encounterResultKeys.has(monitoringDisplayResultKey(tr)),
    );
    for (const tr of cycleResults) {
      const dateISO = tr.date!.split("T")[0]!;
      const a = ensure(dateISO);
      ingestResult(a, tr.name ?? "", resolvedValue(tr), tr.clinicalAssessment);
    }

    // Source 3: clinic-authored labs attached to the phase.
    for (const phase of phases ?? []) {
      for (const tr of phase.testResults ?? []) {
        if (!tr.date) continue;
        if (encounterResultKeys.has(monitoringDisplayResultKey(tr))) continue;
        const dateISO = tr.date.split("T")[0]!;
        const a = ensure(dateISO);
        ingestResult(
          a,
          tr.name ?? "",
          tr.doctorValue ?? tr.patientValue ?? tr.value ?? "",
          tr.clinicalAssessment,
        );
      }
    }

    // Source 4: phase ovary follicles (clinic scans) + lining metric.
    for (const phase of phases ?? []) {
      for (const f of phase.ovaryFollicles ?? []) {
        if (!f.date || f.size == null) continue;
        const dateISO = f.date.split("T")[0]!;
        const a = ensure(dateISO);
        // Only fall back to clinic follicle rows when a result did not
        // already carry the size list for that side.
        if (f.ovarySide === "left" && !a.sizesLFromResult) {
          a.sizesL.push(f.size);
        } else if (f.ovarySide === "right" && !a.sizesRFromResult) {
          a.sizesR.push(f.size);
        }
      }
      const dateISO = phase.startDate ?? "";
      if (!dateISO) continue;
      for (const m of phase.fertilityMetrics ?? []) {
        if (m.value == null) continue;
        if (m.type === "endometrial_thickness") {
          const a = ensure(dateISO);
          if (!a.lining) {
            a.lining = {
              text: String(m.value),
              num: m.value,
              assessment: null,
            };
          }
        }
      }
    }

    // Trigger day(s) from the structured triggerShot.
    const triggerDates = new Set<string>();
    for (const phase of phases ?? []) {
      const d = phase.triggerShot?.date;
      if (d) triggerDates.add(d.split("T")[0]!);
    }

    // Medications active per day. Phase-scoped meds are already tied to this
    // cycle; patient meds are filtered by cycleId.
    const medList = [
      ...(phases ?? []).flatMap((p) => p.patientMedications ?? []),
      ...(medications ?? []).filter((m) => m.cycleId === cycleId),
    ];
    const medById = new Map<string, (typeof medList)[number]>();
    for (const m of medList) if (m.id) medById.set(m.id, m);
    const uniqueMeds = Array.from(medById.values());

    if (acc.size === 0 && !cycleStartDate) return [];

    /**
     * Build a contiguous day range so meds show daily and labs on scan days.
     *
     * Every date is reduced to its calendar day on the way in. These values
     * arrive from several places — the cycle, the medications, the trigger, the
     * phases — and a Postgres timestamp ("2026-06-04 07:35:09+00") or an ISO
     * datetime is not a day. Left whole, one would sort past every real date
     * and become the range's end, and the arithmetic downstream would answer
     * with NaN. Anything that is not a date at all is dropped rather than
     * carried as a poison value.
     */
    const dataDates: string[] = [];
    const pushDay = (value: string | null | undefined) => {
      const day = calendarDayOf(value);
      if (day) dataDates.push(day);
    };
    for (const key of acc.keys()) pushDay(key);
    pushDay(cycleStartDate);
    for (const m of uniqueMeds) pushDay(m.startDate);
    triggerDates.forEach(pushDay);

    /**
     * The sheet runs from the cycle's own D1 across the stimulation, whether or
     * not each day has been reached or recorded yet.
     *
     * The range used to be whatever the data happened to span, widened
     * incidentally by medication start dates — so a cycle with its protocol
     * meds recorded showed a row per day while a cycle without them showed one
     * column per scan. Same component, different shape, for a reason that has
     * nothing to do with the treatment.
     *
     * Every day of the stimulation gets its column, empty or not, because the
     * empty cell is the affordance: it reads "—" and it is what you tap to fill
     * it. Stopping at today left a cycle on its first day as a single column
     * with nothing beside it, which read as a broken table rather than a
     * stimulation about to happen.
     *
     * The horizon is the protocol phase's own end when the clinic recorded one,
     * so the sheet matches the planned protocol rather than a guess. Absent
     * that, DEFAULT_STIM_DAYS days — long enough to cover an ordinary
     * stimulation, short enough that the sheet stays a sheet. Days beyond the
     * horizon that already carry data still widen the range: a protocol running
     * long is a fact about the cycle, not an overflow to hide.
     */
    const protocolPhase = (phases ?? []).find((p) => p.type === "protocol");
    const rangeStart =
      calendarDayOf(cycleStartDate) ?? calendarDayOf(protocolPhase?.startDate);
    if (rangeStart) {
      pushDay(rangeStart);
      const protocolEnd = calendarDayOf(protocolPhase?.endDate);
      const horizon =
        protocolEnd ?? addDaysISO(rangeStart, DEFAULT_STIM_DAYS - 1);
      if (horizon && horizon >= rangeStart) pushDay(horizon);
    }

    if (dataDates.length === 0) return [];
    const minISO = dataDates.reduce((a, b) => (a < b ? a : b));
    const maxISO = dataDates.reduce((a, b) => (a > b ? a : b));
    const referenceStart = calendarDayOf(cycleStartDate) ?? minISO;

    const out: StimDay[] = [];
    const cursor = new Date(minISO + "T00:00:00");
    let guard = 0;
    /**
     * The loop ends on the calendar day, not on a timestamp.
     *
     * Both ends were built at local midnight and compared as instants, which
     * breaks where the clock springs forward at midnight — Havana, Cairo,
     * Beirut, Santiago, the Azores. Local midnight does not exist on that day,
     * so the cursor is normalised to 01:00 and stays an hour ahead of every
     * later midnight, ending the loop one day early and dropping the sheet's
     * last column: the most recent scan, the one she came to read.
     *
     * Comparing the dates as strings asks the question actually being asked —
     * have we passed the last day — and cannot be moved by an hour.
     *
     * The guard is a backstop against a corrupt date, not a display limit: the
     * range is the cycle's own length now, and a long cycle must not lose its
     * most recent days silently. 400 is past any real cycle.
     */
    while (toLocalDateString(cursor) <= maxISO && guard < 400) {
      guard += 1;
      /**
       * Read back in local time, because that is how the cursor was written.
       *
       * `new Date(minISO + "T00:00:00")` is local midnight, and
       * `toISOString()` answers in UTC — so east of UTC every generated row
       * carried the previous day. The accumulator is keyed by the date the
       * monitoring itself claims, so no row ever matched it: with a single
       * scan logged, the sheet built one row dated the day before and reported
       * that nothing had been recorded, while the chart beside it drew the
       * value. The chart cannot lose a date because it builds its days from
       * the dates that carry data; this loop rebuilds a range, and a rebuilt
       * range has to come back as the same day it went in.
       */
      const dateISO = toLocalDateString(cursor);
      cursor.setDate(cursor.getDate() + 1);
      const a = acc.get(dateISO);

      const meds: MedChip[] = uniqueMeds
        .filter((m) => {
          if (!m.startDate) return false;
          const endActive = m.endDate ?? maxISO;
          return m.startDate <= dateISO && dateISO <= endActive;
        })
        .map((m) => {
          const name =
            m.displayName ||
            m.medication?.name ||
            m.rawMedicationName ||
            m.medication?.molecule ||
            t("care.stim_sheet_view.medication_fallback");
          const status = getTodayDoseStatus(
            medicationLogs,
            { medicationId: m.medicationId, medicationName: name },
            dateISO,
          );
          return {
            key: m.id,
            name,
            dose:
              m.dose != null
                ? `${m.dose}${m.doseUnit ? ` ${m.doseUnit}` : ""}`
                : null,
            status,
            isTrigger: isTriggerMedicationName(name),
          };
        });

      const left = a?.sizesL ?? [];
      const right = a?.sizesR ?? [];
      const all = [...left, ...right];
      const lead = all.length > 0 ? Math.max(...all) : null;
      const larger = all.filter((s) => s >= MATURE_MM).length;
      const cohort = all.filter((s) => s >= COHORT_MM).length;
      const follTotal =
        a?.countL != null || a?.countR != null
          ? (a?.countL ?? 0) + (a?.countR ?? 0)
          : all.length > 0
            ? all.length
            : null;

      const startDate = new Date(dateISO + "T00:00:00");
      out.push({
        dateISO,
        stimDay: stimDayNumber(dateISO, referenceStart),
        dayLabel: `D${stimDayNumber(dateISO, referenceStart)}`,
        dateLabel: formatDate(startDate, locale, {
          month: "short",
          day: "numeric",
        }),
        e2: a?.e2 ?? null,
        lh: a?.lh ?? null,
        p4: a?.p4 ?? null,
        lining: a?.lining ?? null,
        follLeft: left,
        follRight: right,
        lead,
        larger,
        cohort,
        follTotal,
        meds,
        isTriggerDay: triggerDates.has(dateISO),
      });
    }

    return out;
  }, [
    testResults,
    monitoringEncounters,
    phases,
    medications,
    medicationLogs,
    cycleId,
    cycleStartDate,
    locale,
    t,
    includeUnlinkedResults,
  ]);

  /**
   * A day counts as recorded if any cell the sheet can show is filled.
   *
   * `follTotal` was missing from this list, and it is the one cell that can be
   * filled on its own: it comes from the follicle counts, which a scan can
   * report without individual sizes. So a patient who logged "2 follicles" —
   * nothing else — had `follLeft` and `follRight` empty, this returned false,
   * and the sheet told her nothing was recorded while the chart beside it drew
   * her count. Two tabs over the same data disagreeing about whether it exists.
   *
   * `lead`, `larger` and `cohort` are all derived from the sizes already
   * checked, so they add nothing here.
   */
  const hasData = days.some(
    (d) =>
      d.e2 ||
      d.lh ||
      d.p4 ||
      d.lining ||
      d.follLeft.length > 0 ||
      d.follRight.length > 0 ||
      d.follTotal != null ||
      d.meds.length > 0,
  );

  useMemo(() => {
    onDataStateChange?.(hasData);
  }, [hasData, onDataStateChange]);

  if (!hasData) {
    return (
      <ActionRowCard
        title={t("care.stim_sheet_view.no_monitoring_logged")}
        subtitle={t("care.stim_sheet_view.monitoring_empty_prompt")}
        leading={
          <View
            style={{
              width: 38,
              height: 38,
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
            }}
          >
            <Image
              source={MONITORING_ILLUSTRATION}
              resizeMode="contain"
              style={{ width: 58, height: 58 }}
            />
          </View>
        }
        trailing={
          onAddDay ? (
            <Plus size={18} color={EMBER.colors.textLight} strokeWidth={1.5} />
          ) : null
        }
        onPress={onAddDay}
        style={{ marginTop: 12 }}
      />
    );
  }

  // One row per distinct medication, labeled by drug name in the frozen
  // column so the drug is always visible; the cells carry the daily dose.
  const medRows: SheetRow[] = [];
  const seenMeds = new Set<string>();
  for (const day of days) {
    for (const m of day.meds) {
      if (!seenMeds.has(m.key)) {
        seenMeds.add(m.key);
        medRows.push({ key: `med:${m.key}`, label: m.name, height: 34 });
      }
    }
  }
  // Ordered outcome-first: people read the follicle/lining response, then the
  // hormones behind it, then the medications (the inputs) at the bottom.
  const rows: SheetRow[] = [
    {
      key: "follicles",
      label: t("care.stim_sheet_view.row_follicles"),
      height: 74,
    },
    { key: "lining", label: t("care.stim_sheet_view.row_lining"), height: 34 },
    { key: "e2", label: t("care.stim_sheet_view.row_e2"), height: 42 },
    { key: "lh", label: t("care.stim_sheet_view.row_lh"), height: 34 },
    { key: "p4", label: t("care.stim_sheet_view.row_p4"), height: 34 },
    ...medRows,
  ];

  // Cycle outcome (retrieved / mature / fertilized). Only present once a
  // retrieval has happened, so this block appears on completed/past cycles
  // and makes the journey from stimulation to result explicit.
  const outcomeTiles = buildOutcomeTiles(phases, t);

  return (
    <View style={{ marginTop: 12, gap: 12 }}>
      <View style={{ ...GLASS_CARD_STYLE, padding: 0, overflow: "hidden" }}>
        <View style={{ flexDirection: "row" }}>
          {/* Frozen label column */}
          <View
            style={{
              width: LABEL_W,
              borderRightWidth: 1,
              borderRightColor: "rgba(0,0,0,0.05)",
            }}
          >
            <View style={{ height: 58 }} />
            {rows.map((row, i) => (
              <View
                key={row.key}
                style={{
                  height: row.height,
                  justifyContent: "center",
                  paddingHorizontal: 12,
                  backgroundColor:
                    i % 2 === 0 ? "rgba(0,0,0,0.015)" : "transparent",
                }}
              >
                <Text
                  className="font-dm-sans"
                  style={{ fontSize: 11, fontWeight: "600", color: TEXT_MID }}
                >
                  {row.label}
                </Text>
              </View>
            ))}
          </View>

          {/* Scrollable day columns */}
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            scrollEventThrottle={16}
            onLayout={(e) => setViewportW(e.nativeEvent.layout.width)}
            onContentSizeChange={(w) => setContentW(w)}
            onScroll={(e) => setScrollX(e.nativeEvent.contentOffset.x)}
          >
            <View style={{ flexDirection: "row" }}>
              {days.map((day) => (
                <DayColumn
                  key={day.dateISO}
                  t={t}
                  day={day}
                  rows={rows}
                  onEditCell={onEditCell}
                />
              ))}
            </View>
          </ScrollView>
        </View>

        {/* Right-edge fade that signals more days can be scrolled into view. */}
        {contentW - viewportW - scrollX > 4 ? (
          <LinearGradient
            pointerEvents="none"
            colors={["rgba(252,250,247,0)", "rgba(252,250,247,0.96)"]}
            start={{ x: 0, y: 0.5 }}
            end={{ x: 1, y: 0.5 }}
            style={{
              position: "absolute",
              right: 0,
              top: 0,
              bottom: 0,
              width: 48,
            }}
          />
        ) : null}
      </View>

      {outcomeTiles.length > 0 ? (
        <OutcomeCard
          title={t("care.stim_sheet_view.outcome_title")}
          subtitle={t("care.stim_sheet_view.outcome_subtitle")}
          tiles={outcomeTiles}
        />
      ) : null}
    </View>
  );
}

function buildOutcomeTiles(
  phases: ApiPhase[],
  t: ReturnType<typeof useTranslations>,
): { label: string; value: number }[] {
  const metrics = new Map<string, number>();
  for (const phase of phases ?? []) {
    for (const fm of phase.fertilityMetrics ?? []) {
      if (fm.value != null && fm.type) {
        metrics.set(fm.type, fm.value);
      }
    }
  }
  const tiles: { label: string; value: number }[] = [];
  const add = (type: string, label: string) => {
    const value = metrics.get(type);
    if (value != null) {
      tiles.push({ label, value });
    }
  };
  add("retrieved_oocytes", t("care.stim_sheet_view.outcome_retrieved"));
  add("m2_count", t("care.stim_sheet_view.outcome_mature"));
  add("fertilized_oocytes", t("care.stim_sheet_view.outcome_fertilized"));
  add("embryos_day_5", t("care.stim_sheet_view.outcome_blastocysts"));
  add("frozen_embryo", t("care.stim_sheet_view.outcome_frozen"));
  return tiles;
}

function OutcomeCard({
  title,
  subtitle,
  tiles,
}: {
  title: string;
  subtitle: string;
  tiles: { label: string; value: number }[];
}) {
  return (
    <View
      style={{ ...GLASS_CARD_STYLE, borderRadius: 18, padding: 15, gap: 13 }}
    >
      <View style={{ flexDirection: "row", alignItems: "center", gap: 12 }}>
        <OrganicMiniIcon kind="eggs" size={42} />
        <View style={{ flex: 1 }}>
          <CardTitleText>{title}</CardTitleText>
          <CardSubtitleText>{subtitle}</CardSubtitleText>
        </View>
      </View>
      <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 9 }}>
        {tiles.map((tile) => (
          <View
            key={tile.label}
            style={{
              flexGrow: 1,
              flexBasis: 96,
              minWidth: 90,
              borderRadius: 14,
              backgroundColor: "rgba(255,255,255,0.38)",
              borderWidth: 1,
              borderColor: "rgba(92,52,32,0.08)",
              padding: 12,
            }}
          >
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 22,
                fontWeight: "800",
                color: EMBER.colors.text,
              }}
            >
              {String(tile.value)}
            </Text>
            <Text
              className="font-dm-sans"
              numberOfLines={1}
              style={{
                fontSize: 11,
                fontWeight: "700",
                color: TEXT_LIGHT,
                marginTop: 3,
              }}
            >
              {tile.label}
            </Text>
          </View>
        ))}
      </View>
    </View>
  );
}

function labColor(cell: LabCell | null): string {
  if (!cell) {
    return TEXT_LIGHT;
  }
  if (cell.assessment === "low" || cell.assessment === "high") {
    return ABNORMAL;
  }
  return EMBER.colors.text;
}

function LabValue({ cell }: { cell: LabCell | null }) {
  if (!cell || cell.text === "") {
    return (
      <Text
        className="font-dm-sans"
        style={{ fontSize: 12, color: EMBER.colors.textFaint }}
      >
        {"—"}
      </Text>
    );
  }
  return (
    <Text
      className="font-dm-sans"
      style={{ fontSize: 13, fontWeight: "600", color: labColor(cell) }}
    >
      {cell.text}
    </Text>
  );
}

function FollicleCohort({ left, right }: { left: number[]; right: number[] }) {
  const all = [...left, ...right];
  if (all.length === 0) {
    return (
      <Text
        className="font-dm-sans"
        style={{ fontSize: 12, color: EMBER.colors.textFaint }}
      >
        {"—"}
      </Text>
    );
  }
  const maxSize = Math.max(...all, 22);
  const sorted = [...all].sort((a, b) => b - a).slice(0, 8);
  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "flex-end",
        gap: 2,
        height: 30,
      }}
    >
      {sorted.map((size, i) => {
        const h = Math.max(4, Math.round((size / maxSize) * 30));
        const color =
          size >= MATURE_MM ? LARGER : size >= COHORT_MM ? MID_RANGE : SMALLER;
        return (
          <View
            key={i}
            style={{
              width: 5,
              height: h,
              borderRadius: 2,
              backgroundColor: color,
            }}
          />
        );
      })}
    </View>
  );
}

function DayColumn({
  t,
  day,
  rows,
  onEditCell,
}: {
  t: ReturnType<typeof useTranslations>;
  day: StimDay;
  rows: SheetRow[];
  onEditCell?: (dateISO: string, cellKey: string) => void;
}) {
  const capturePress = usePressCapture("DayColumn");
  return (
    <View
      style={{
        width: DAY_W,
        borderRightWidth: 1,
        borderRightColor: "rgba(0,0,0,0.04)",
        backgroundColor: day.isTriggerDay
          ? "rgba(184,74,22,0.05)"
          : "transparent",
      }}
    >
      {/* Header */}
      <View
        style={{ height: 58, alignItems: "center", justifyContent: "center" }}
      >
        <Text
          className="font-dm-sans"
          style={{ fontSize: 12, fontWeight: "700", color: COPPER }}
        >
          {day.dayLabel}
        </Text>
        <Text
          className="font-dm-sans"
          style={{ fontSize: 10, color: TEXT_LIGHT }}
        >
          {day.dateLabel}
        </Text>
      </View>

      {rows.map((row, i) => {
        const bg = i % 2 === 0 ? "rgba(0,0,0,0.015)" : "transparent";
        const isEditableCell =
          Boolean(onEditCell) && !row.key.startsWith("med:");
        const cellTestId = `monitoring-cell-${day.dateISO}-${row.key.replace(
          /[^a-zA-Z0-9_-]/g,
          "-",
        )}`;
        return (
          <Pressable
            key={row.key}
            testID={cellTestId}
            onPress={
              isEditableCell
                ? () => {
                    capturePress(
                      pressLabel(
                        `${row.label}, ${day.dayLabel}, ${day.dateLabel}`,
                      ),
                      "monitoring_cell",
                    );
                    onEditCell?.(day.dateISO, row.key);
                  }
                : undefined
            }
            accessibilityRole={isEditableCell ? "button" : undefined}
            accessibilityLabel={
              isEditableCell
                ? `${row.label}, ${day.dayLabel}, ${day.dateLabel}`
                : undefined
            }
            style={({ pressed }) => ({
              height: row.height,
              alignItems: "center",
              justifyContent: "center",
              paddingHorizontal: 4,
              backgroundColor: pressed ? "rgba(184,74,22,0.10)" : bg,
            })}
          >
            {row.key.startsWith("med:") ? (
              <MedDoseCell day={day} medKey={row.key.slice(4)} />
            ) : row.key === "follicles" ? (
              <View style={{ alignItems: "center", gap: 3 }}>
                <FollicleCohort left={day.follLeft} right={day.follRight} />
                {day.lead != null ? (
                  <Text
                    className="font-dm-sans"
                    style={{ fontSize: 9, color: TEXT_MID }}
                  >
                    {t("care.stim_sheet_view.follicle_larger_summary", {
                      params: {
                        lead: String(day.lead),
                        larger: String(day.larger),
                      },
                    })}
                  </Text>
                ) : null}
              </View>
            ) : (
              <LabValue cell={labCellFor(day, row.key)} />
            )}
          </Pressable>
        );
      })}
    </View>
  );
}

function labCellFor(day: StimDay, key: string): LabCell | null {
  if (key === "e2") {
    return day.e2;
  }
  if (key === "lh") {
    return day.lh;
  }
  if (key === "p4") {
    return day.p4;
  }
  if (key === "lining") {
    return day.lining;
  }
  return null;
}

function MedDoseCell({ day, medKey }: { day: StimDay; medKey: string }) {
  const med = day.meds.find((m) => m.key === medKey);
  if (!med) {
    return (
      <Text
        className="font-dm-sans"
        style={{ fontSize: 12, color: EMBER.colors.textFaint }}
      >
        {"—"}
      </Text>
    );
  }
  return (
    <View style={{ flexDirection: "row", alignItems: "center", gap: 3 }}>
      {med.status ? (
        <View
          style={{
            width: 5,
            height: 5,
            borderRadius: 3,
            backgroundColor: STATUS_COLOR[med.status] ?? TEXT_LIGHT,
          }}
        />
      ) : null}
      <Text
        className="font-dm-sans"
        numberOfLines={1}
        style={{ fontSize: 12, color: EMBER.colors.text, maxWidth: DAY_W - 12 }}
      >
        {med.dose ?? "•"}
      </Text>
    </View>
  );
}
